package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.util.AppClock.now

class NotifyRelevantEmployeesUseCase(
    private val userRepository: UserRepository,
    private val availabilityRepository: AvailabilityRepository
) {
    suspend operator fun invoke(
        workplaceId: String,
        shift: Shift,
        title: String,
        message: String,
        notificationType: NotificationType
    ): Result<Unit> {
        return try {
            val employees = userRepository.getAssignableEmployees(workplaceId).getOrThrow()
            
            // 1. Filter by role and skills
            val qualifiedEmployees = employees.filter { user ->
                val engagement = user.engagements[workplaceId] ?: return@filter false
                val roleMatch = shift.requiredRole == null || engagement.role == shift.requiredRole
                val skillsMatch = shift.requiredSkills.all { it in engagement.skills }
                // Shadow profiles (invited, not joined yet) have nobody to read a notification.
                engagement.isActive && roleMatch && skillsMatch && user.uid != shift.userId && !user.isShadow
            }

            // 2. Filter by availability
            val shiftDate = shift.date // "2026-02-01"
            val month = shiftDate.substring(0, 7) // "2026-02"
            val dayOfMonth = shiftDate.substring(8, 10).toInt()

            val now = now().toString()

            qualifiedEmployees.forEach { employee ->
                // Only managers may read someone's availability (firestore.rules): an employee putting
                // a shift on the marketplace cannot, and a month not filled in is unknown too. Only
                // a day marked UNAVAILABLE leaves someone out; unknown is not a reason to stay silent.
                val availability = availabilityRepository.getAvailability(employee.uid, workplaceId, month).getOrNull()
                val isUnavailable = availability?.days?.get(dayOfMonth)?.status == AvailabilityStatus.UNAVAILABLE

                if (!isUnavailable) {
                    val notification = Notification(
                        userId = employee.uid,
                        title = title,
                        message = message,
                        type = notificationType,
                        createdAt = now,
                        data = NotificationData.forShift(shift, workplaceId, NotificationData.KIND to NotificationData.Kind.MARKETPLACE)
                    )
                    userRepository.sendNotification(notification)
                }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
